import { useState, useEffect } from "react";
import { ArrowLeft, Pencil, Trash2, Plus, MapPin, ScanLine, FileText } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import api from "../api.js";
import { DocModal } from "./DocumentsPage.jsx";
import CatalogPickerModal from "../components/CatalogPickerModal.jsx";
import YClientsRecordPickerModal from "../components/YClientsRecordPickerModal.jsx";
import { YclientsLinkField, ScanRecordCell } from "../components/PatientYclientsLink.jsx";
import ProductQuantityPriceRow from "../components/ProductQuantityPriceRow.jsx";
import { buildProductGroupTree } from "../lib/productGroups.jsx";
import { useAuth } from "../contexts/AuthContext.jsx";
import { mskTodayStr } from "../lib/mskDate.js";
import PatientAlias from "../components/PatientAlias.jsx";

function mmToRuSize(mm) {
  if (!mm || isNaN(Number(mm))) return null;
  return Math.round(Number(mm) / 10 * 1.5 + 1);
}

function formatPrice(price) {
  if (price === null || price === undefined || price === "") return "";
  return new Intl.NumberFormat("ru-RU").format(price) + " ₽";
}



function PatientField({ label, value, isLink }) {
  return (
    <div className="flex items-baseline gap-2">
      <span className="text-xs text-slate-400 w-28 flex-shrink-0">{label}</span>
      {value ? (
        isLink ? (
          <a href={value} target="_blank" rel="noreferrer" className="text-sm text-blue-600 hover:underline break-all">{value}</a>
        ) : (
          <span className="text-sm text-slate-900 break-words">{value}</span>
        )
      ) : (
        <span className="text-sm text-slate-300">—</span>
      )}
    </div>
  );
}

function PatientInfoCard({ patient, onUpdate, canManageYclients, onYclientsLinked, onMerged }) {
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState({
    name: patient.name || "",
    phone: patient.phone || "",
    birthDate: patient.birthDate || "",
    crmLink: patient.crmLink || "",
    socialNetwork: patient.socialNetwork || "",
    internalNote: patient.internalNote || "",
  });
  const [saving, setSaving] = useState(false);

  const handleSave = async () => {
    if (!form.name.trim()) return;
    setSaving(true);
    try {
      await api.updatePatient(patient.id, form);
      onUpdate(form);
      setEditing(false);
    } catch {}
    finally { setSaving(false); }
  };

  const handleCancel = () => {
    setForm({
      name: patient.name || "",
      phone: patient.phone || "",
      birthDate: patient.birthDate || "",
      crmLink: patient.crmLink || "",
      socialNetwork: patient.socialNetwork || "",
      internalNote: patient.internalNote || "",
    });
    setEditing(false);
  };

  const birthDateDisplay = patient.birthDate
    ? new Date(patient.birthDate + "T00:00:00").toLocaleDateString("ru-RU")
    : null;

  return (
    <div className="flex-1 min-w-0 rounded-xl border border-slate-200 bg-white shadow-sm p-5 flex flex-col gap-3">
      <div className="flex items-center justify-between">
        <span className="text-xs font-semibold text-slate-500 uppercase tracking-wide">Данные пациента</span>
        {!editing && (
          <Button variant="ghost" size="sm" className="gap-1.5" onClick={() => setEditing(true)}>
            <Pencil className="h-3.5 w-3.5" />
            Редактировать
          </Button>
        )}
      </div>

      {editing ? (
        <div className="flex flex-col gap-2.5">
          <div className="flex items-center gap-2">
            <label className="text-xs text-slate-400 w-28 flex-shrink-0">ФИО</label>
            <Input
              className="h-8 text-xs"
              value={form.name}
              autoFocus
              onChange={e => setForm(f => ({ ...f, name: e.target.value }))}
              onKeyDown={e => e.key === "Enter" && handleSave()}
            />
          </div>
          <div className="flex items-center gap-2">
            <label className="text-xs text-slate-400 w-28 flex-shrink-0">Телефон</label>
            <Input
              className="h-8 text-xs"
              value={form.phone}
              placeholder="+7 999 123 45 67"
              onChange={e => setForm(f => ({ ...f, phone: e.target.value }))}
              onKeyDown={e => e.key === "Enter" && handleSave()}
            />
          </div>
          <div className="flex items-center gap-2">
            <label className="text-xs text-slate-400 w-28 flex-shrink-0">Дата рождения</label>
            <Input
              type="date"
              className="h-8 text-xs"
              value={form.birthDate}
              onChange={e => setForm(f => ({ ...f, birthDate: e.target.value }))}
            />
          </div>
          <div className="flex items-center gap-2">
            <label className="text-xs text-slate-400 w-28 flex-shrink-0">Соц. сеть</label>
            <Input
              className="h-8 text-xs"
              value={form.socialNetwork}
              placeholder="ссылка или ник"
              onChange={e => setForm(f => ({ ...f, socialNetwork: e.target.value }))}
              onKeyDown={e => e.key === "Enter" && handleSave()}
            />
          </div>
          <div className="flex items-start gap-2">
            <label className="text-xs text-slate-400 w-28 flex-shrink-0 pt-1.5">Скрытая заметка</label>
            <textarea
              className="flex-1 text-xs rounded-md border border-slate-200 px-2.5 py-1.5 resize-none"
              rows={2}
              value={form.internalNote}
              placeholder="Видно только персоналу, не попадает в отчёт"
              onChange={e => setForm(f => ({ ...f, internalNote: e.target.value }))}
            />
          </div>
          <div className="flex gap-2 mt-1">
            <Button size="sm" disabled={saving || !form.name.trim()} onClick={handleSave}>
              {saving ? "..." : "Сохранить"}
            </Button>
            <Button size="sm" variant="ghost" onClick={handleCancel}>Отмена</Button>
          </div>
        </div>
      ) : (
        <div className="flex flex-col gap-2.5">
          <PatientField label="ФИО" value={patient.name} />
          {patient.alias && patient.alias !== patient.name && <PatientField label="Псевдоним (в отчёте)" value={patient.alias} />}
          <PatientField label="Телефон" value={patient.phone} />
          <PatientField label="Дата рождения" value={birthDateDisplay} />
          <YclientsLinkField patient={patient} canManage={canManageYclients} onLinked={onYclientsLinked} onMerged={onMerged} />
          <PatientField label="Соц. сеть" value={patient.socialNetwork} />
          <PatientField label="Добавлен" value={new Date(patient.createdAt).toLocaleDateString("ru-RU")} />
          {patient.internalNote && (
            <div className="flex items-baseline gap-2">
              <span className="text-xs text-slate-400 w-28 flex-shrink-0">Скрытая заметка</span>
              <span className="text-sm text-amber-700 bg-amber-50 rounded-md px-2 py-1 break-words flex-1">{patient.internalNote}</span>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function PurchasesCard({ patient, initialPurchases }) {
  const { user } = useAuth();
  const patientId = patient.id;
  const [purchases, setPurchases] = useState(initialPurchases || []);
  const [pickerOpen, setPickerOpen] = useState(false);
  const [pickedIds, setPickedIds] = useState([]);
  const [products, setProducts] = useState([]);
  const [productGroups, setProductGroups] = useState([]);
  const [recordPicker, setRecordPicker] = useState(null); // { records, details } while open, null when closed
  // Количество+цена по каждому picked-товару, пока открыта панель подтверждения (между закрытием
  // CatalogPickerModal и реальным созданием покупок) — null значит панель закрыта.
  const [reviewDetails, setReviewDetails] = useState(null);

  const openPicker = () => {
    setPickedIds([]);
    Promise.all([api.listProducts(), api.listProductGroups()])
      .then(([p, g]) => { setProducts(p); setProductGroups(g); })
      .catch(() => {});
    setPickerOpen(true);
  };

  const togglePicked = (id) => {
    setPickedIds(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);
  };

  // details — { [productId]: { quantity, price } }; несколько одинаковых товаров = несколько
  // независимых вызовов addPurchase (один товар = одна покупка = одна YClients-позиция, как и
  // раньше), а не новое поле "количество" на самой покупке.
  const addPickedPurchases = async (yclientsRecordId, details) => {
    const today = mskTodayStr();
    const picked = products.filter(p => pickedIds.includes(p.id));
    for (const p of picked) {
      const { quantity = 1, price = p.price ?? null } = details?.[p.id] || {};
      for (let i = 0; i < quantity; i++) {
        try {
          const purchase = await api.addPurchase(patientId, { name: p.name, price, date: today, productId: p.id, yclientsRecordId });
          setPurchases(prev => [purchase, ...prev]);
        } catch { /* one failed pick — skip it, rest still get added */ }
      }
    }
    if (yclientsRecordId) {
      await api.payPurchaseRecord(patientId, yclientsRecordId).catch(() => {});
    }
  };

  const closePickerAndReview = () => {
    setPickerOpen(false);
    if (!pickedIds.length) return;
    const initial = {};
    for (const id of pickedIds) {
      const p = products.find(x => x.id === id);
      initial[id] = { quantity: 1, price: p?.price ?? null };
    }
    setReviewDetails(initial);
  };

  const proceedAfterReview = async () => {
    const details = reviewDetails;
    setReviewDetails(null);
    if (!patient?.yclientsClientId) {
      await addPickedPurchases(null, details);
      return;
    }
    setRecordPicker({ records: null, details });
    (user?.isCentral
      ? api.getYclientsClientRecordsAdmin(patient.yclientsClientId, patient.yclientsCompanyId)
      : api.getYclientsClientRecords(patient.yclientsClientId)
    ).then(({ records }) => setRecordPicker({ records, details }))
      .catch(() => setRecordPicker({ records: [], details }));
  };

  const handleRecordPick = async (yclientsRecordId) => {
    const details = recordPicker?.details;
    setRecordPicker(null);
    await addPickedPurchases(yclientsRecordId || null, details);
  };

  const handleDelete = async (id) => {
    await api.deletePurchase(patientId, id);
    setPurchases(prev => prev.filter(p => p.id !== id));
  };

  const groupTree = buildProductGroupTree(products, productGroups);

  return (
    <div className="flex-1 min-w-0 rounded-xl border border-slate-200 bg-white shadow-sm p-5 flex flex-col gap-3">
      <div className="flex items-center justify-between">
        <span className="text-xs font-semibold text-slate-500 uppercase tracking-wide">Покупки ({purchases.length})</span>
        <div className="flex items-center gap-2">
          <Button size="sm" className="gap-1.5" onClick={openPicker}>
            <Plus className="h-3.5 w-3.5" />
            Добавить
          </Button>
        </div>
      </div>

      {purchases.length === 0 && (
        <div className="text-sm text-slate-300">Нет покупок</div>
      )}

      <div className="flex flex-col max-h-72 overflow-y-auto">
        {purchases.map(p => (
          <div key={p.id} className="group flex items-center gap-2 py-2.5 border-b border-slate-50 last:border-0">
            <div className="flex-1 flex flex-wrap items-center gap-2.5 min-w-0">
              <span className="text-sm font-medium text-slate-900">{p.name}</span>
              {p.price !== null && p.price !== undefined && (
                <span className="text-sm font-semibold text-green-600">{formatPrice(p.price)}</span>
              )}
              {p.date && (
                <span className="text-xs text-slate-400">
                  {new Date(p.date + "T00:00:00").toLocaleDateString("ru-RU")}
                </span>
              )}
            </div>
            <Button
              variant="ghost"
              size="icon"
              className="h-7 w-7 text-slate-300 opacity-0 group-hover:opacity-100 hover:text-red-600 hover:bg-red-50"
              onClick={() => handleDelete(p.id)}
              title="Удалить"
            >
              <Trash2 className="h-3.5 w-3.5" />
            </Button>
          </div>
        ))}
      </div>

      {pickerOpen && (
        <CatalogPickerModal
          title="Выбрать товары и стельки"
          groupTree={groupTree}
          selectedIds={pickedIds}
          onToggle={togglePicked}
          onClose={closePickerAndReview}
        />
      )}

      {reviewDetails && (
        <PurchaseReviewModal
          items={products.filter(p => pickedIds.includes(p.id))}
          details={reviewDetails}
          onDetailsChange={(id, patch) => setReviewDetails(prev => ({ ...prev, [id]: { ...prev[id], ...patch } }))}
          onCancel={() => setReviewDetails(null)}
          onConfirm={proceedAfterReview}
        />
      )}

      {recordPicker && (
        <YClientsRecordPickerModal
          client={{ name: patient.name }}
          records={recordPicker.records}
          onPick={handleRecordPick}
          onCancel={() => setRecordPicker(null)}
        />
      )}
    </div>
  );
}

function PurchaseReviewModal({ items, details, onDetailsChange, onCancel, onConfirm }) {
  const grandTotal = items.reduce((s, p) => {
    const d = details[p.id] || { quantity: 1, price: p.price ?? null };
    return s + (d.price != null ? d.price * (d.quantity ?? 1) : 0);
  }, 0);
  return (
    <div
      className="fixed inset-0 bg-black/50 z-[210] flex items-center justify-center p-4"
      onMouseDown={e => e.target === e.currentTarget && onCancel()}
    >
      <div className="bg-white rounded-2xl shadow-2xl w-full overflow-hidden flex flex-col" style={{ maxWidth: 480, maxHeight: "85vh" }}>
        <div className="px-5 py-4 border-b border-slate-100 flex-shrink-0">
          <span className="text-base font-bold text-slate-900">Количество и цена</span>
        </div>
        <div className="px-5 py-4 overflow-y-auto flex flex-col gap-2.5">
          {items.map(p => {
            const d = details[p.id] || { quantity: 1, price: p.price ?? null };
            return (
              <ProductQuantityPriceRow
                key={p.id}
                name={p.name}
                catalogPrice={p.price}
                details={d}
                onChange={patch => onDetailsChange(p.id, patch)}
              />
            );
          })}
        </div>
        <div className="flex items-center justify-between gap-2 px-5 py-3 border-t border-slate-100 flex-shrink-0">
          <span className="text-sm font-semibold text-slate-600">Итого: {formatPrice(grandTotal)}</span>
          <div className="flex gap-2">
            <Button size="sm" variant="outline" onClick={onCancel}>Отмена</Button>
            <Button size="sm" onClick={onConfirm}>Добавить</Button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default function PatientCard({ patientId, onBack, onEditDocument, onStartScan }) {
  const { user } = useAuth();
  const [patient, setPatient] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    api.getPatient(patientId)
      .then(p => { if (!cancelled) setPatient(p); })
      .catch(() => { if (!cancelled) setPatient(null); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [patientId]);

  const handlePatientUpdate = (fields) => {
    setPatient(prev => ({ ...prev, ...fields }));
  };

  // Привязка к YClients и записям — только central-admin (как «Разобрать»).
  const canManageYclients = !!(user?.isCentral && user?.role === "admin");
  const [records, setRecords] = useState(null);
  const [recordPickerScan, setRecordPickerScan] = useState(null);
  const [recordError, setRecordError] = useState("");
  const [recordsFailed, setRecordsFailed] = useState(false);

  const loadRecords = (p) => {
    if (!canManageYclients || !p?.yclientsClientId || !p.yclientsCompanyId) return Promise.resolve(null);
    setRecords(null);
    return api.getYclientsClientRecordsAdmin(p.yclientsClientId, p.yclientsCompanyId)
      .then(r => { setRecordsFailed(false); setRecords(r.records || []); return r.records || []; })
      .catch(() => { setRecordsFailed(true); setRecords([]); return []; });
  };

  useEffect(() => {
    if (!canManageYclients || !patient?.yclientsClientId || !patient.yclientsCompanyId) return;
    let cancelled = false;
    api.getYclientsClientRecordsAdmin(patient.yclientsClientId, patient.yclientsCompanyId)
      .then(r => { if (!cancelled) { setRecordsFailed(false); setRecords(r.records || []); } })
      .catch(() => { if (!cancelled) { setRecordsFailed(true); setRecords([]); } });
    return () => { cancelled = true; };
  }, [canManageYclients, patient?.yclientsClientId, patient?.yclientsCompanyId]);

  const reloadPatient = async (id = patientId) => {
    const p = await api.getPatient(id);
    setPatient(p);
  };

  const updateScan = (scanId, fields) =>
    setPatient(prev => ({ ...prev, scans: (prev.scans || []).map(s => s.id === scanId ? { ...s, ...fields } : s) }));

  const handlePickRecord = async (recordId) => {
    const scan = recordPickerScan;
    setRecordPickerScan(null);
    if (!scan || !recordId) return;
    setRecordError("");
    try {
      const doc = await api.setDocumentYclientsRecord(scan.id, recordId);
      updateScan(scan.id, { yclientsRecordId: doc.yclientsRecordId, yclientsRecordSkippedAt: doc.yclientsRecordSkippedAt });
    } catch (err) {
      setRecordError(err.response?.data?.error || "Не удалось привязать запись к обследованию");
    }
  };

  const handleSetSkipped = async (scan, skipped) => {
    setRecordError("");
    try {
      const doc = await api.setDocumentYclientsSkipped(scan.id, skipped);
      updateScan(scan.id, { yclientsRecordSkippedAt: doc.yclientsRecordSkippedAt });
    } catch (err) {
      setRecordError(err.response?.data?.error || "Не удалось изменить обследование");
    }
  };

  const openRecordPicker = (scan) => {
    setRecordPickerScan(scan);
    if (records === null || records.length === 0) loadRecords(patient);
  };

  if (loading) return <div className="p-10 text-sm text-slate-400">Загрузка...</div>;
  if (!patient) return <div className="p-10 text-sm text-slate-400">Пациент не найден</div>;

  return (
    <div className="flex flex-col flex-1 min-w-0 overflow-hidden">
      <div className="flex items-center gap-3.5 px-5 py-3.5 bg-white border-b border-slate-200 flex-shrink-0 flex-wrap">
        <Button variant="ghost" size="sm" className="gap-1.5" onClick={onBack}>
          <ArrowLeft className="h-3.5 w-3.5" />
          Назад
        </Button>
        <div className="flex items-center justify-center h-10 w-10 rounded-full bg-gradient-to-br from-blue-100 to-blue-200 text-blue-600 text-base font-bold flex-shrink-0">
          {patient.name[0]?.toUpperCase()}
        </div>
        <h2 className="text-lg font-bold text-slate-900">{patient.name}<PatientAlias name={patient.name} alias={patient.alias} className="text-xs" /></h2>
        {patient.location && (
          <span className="inline-flex items-center gap-1 text-xs font-medium text-slate-500 bg-slate-100 rounded-full px-2 py-0.5">
            <MapPin className="h-3 w-3" />
            {patient.location}
          </span>
        )}
        {onStartScan && !user?.isCentral && user?.role !== "manager" && (
          <Button size="sm" className="gap-1.5 ml-auto" onClick={() => onStartScan(patient.id)}>
            <ScanLine className="h-3.5 w-3.5" />
            Начать сканирование
          </Button>
        )}
      </div>

      <div className="flex-1 overflow-y-auto flex flex-col">
        <div className="flex gap-5 px-5 pt-5 items-start flex-wrap">
          <PatientInfoCard
            patient={patient}
            onUpdate={handlePatientUpdate}
            canManageYclients={canManageYclients}
            onYclientsLinked={() => reloadPatient(patient.id)}
            onMerged={(survivorId) => reloadPatient(survivorId)}
          />
          <PurchasesCard patient={patient} initialPurchases={patient.purchases || []} />
        </div>

        <div className="px-5 py-5">
          <h3 className="text-base font-semibold text-slate-900 mb-4">
            Обследования ({patient.scans?.length || 0})
          </h3>
          {recordError && <p className="text-xs text-red-600 mb-3">{recordError}</p>}

          {(!patient.scans || patient.scans.length === 0) && (
            <div className="py-5 text-sm text-slate-400">Нет сканирований для этого пациента</div>
          )}

          {patient.scans?.length > 0 && (
            <div className="overflow-x-auto rounded-xl border border-slate-200 bg-white">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-slate-100 text-left text-[10px] font-semibold text-slate-400 uppercase tracking-wide">
                    <th className="px-4 py-2.5">Дата</th>
                    <th className="px-4 py-2.5">Стопа</th>
                    <th className="px-4 py-2.5">Размер</th>
                    <th className="px-4 py-2.5">Точка</th>
                    <th className="px-4 py-2.5">Провёл</th>
                    <th className="px-4 py-2.5">Отметки</th>
                    <th className="px-4 py-2.5">Запись YClients</th>
                    <th className="px-4 py-2.5 w-12"></th>
                  </tr>
                </thead>
                <tbody>
                  {patient.scans.map(scan => (
                    <tr
                      key={scan.id}
                      className="group border-b border-slate-50 last:border-0 hover:bg-slate-50 cursor-pointer transition-colors"
                      onClick={() => setSelected(scan)}
                    >
                      <td className="px-4 py-2.5 whitespace-nowrap">
                        <div className="flex items-center gap-1.5">
                          {scan.syncedAt && (
                            <span className="h-1.5 w-1.5 rounded-full bg-green-400 flex-shrink-0" title={`Синхронизировано: ${new Date(scan.syncedAt).toLocaleString("ru-RU")}`} />
                          )}
                          {new Date(scan.createdAt).toLocaleString("ru-RU")}
                        </div>
                      </td>
                      <td className="px-4 py-2.5">{scan.footSide ? (scan.footSide === "left" ? "Левая" : "Правая") : <span className="text-slate-300">—</span>}</td>
                      <td className="px-4 py-2.5 whitespace-nowrap">{scan.footSizeMm ? `${scan.footSizeMm} мм (RU ${mmToRuSize(scan.footSizeMm)})` : <span className="text-slate-300">—</span>}</td>
                      <td className="px-4 py-2.5">{scan.location || <span className="text-slate-300">—</span>}</td>
                      <td className="px-4 py-2.5 text-xs text-slate-500">{scan.operatorName || <span className="text-slate-300">—</span>}</td>
                      <td className="px-4 py-2.5">
                        {scan.diseases?.length > 0 ? (
                          <div className="flex flex-wrap gap-1">
                            {scan.diseases.slice(0, 2).map(d => (
                              <span key={d} className="inline-block px-2 py-0.5 bg-red-50 text-red-800 rounded-full text-[10px] font-medium">{d}</span>
                            ))}
                            {scan.diseases.length > 2 && <span className="text-[10px] text-slate-400">+{scan.diseases.length - 2}</span>}
                          </div>
                        ) : <span className="text-slate-300">—</span>}
                      </td>
                      <td className="px-4 py-2.5">
                        <ScanRecordCell
                          scan={scan}
                          records={records}
                          canManage={canManageYclients}
                          patientLinked={!!patient.yclientsClientId}
                          onPickRecord={openRecordPicker}
                          onSetSkipped={handleSetSkipped}
                        />
                      </td>
                      <td className="px-4 py-2.5">
                        {scan.hasReport && (
                          <span className="flex items-center justify-center h-6 w-6 text-blue-600" title="Отчёт сохранён">
                            <FileText className="h-3.5 w-3.5" />
                          </span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {recordPickerScan && (
        <YClientsRecordPickerModal
          client={{ name: patient.name }}
          records={records}
          onPick={handlePickRecord}
          onCancel={() => setRecordPickerScan(null)}
          emptyActionLabel="Закрыть"
          emptyText={recordsFailed ? "Не удалось загрузить записи из YClients — попробуйте позже" : "У этого клиента нет записей в YClients"}
        />
      )}

      {selected && (
        <DocModal
          doc={selected}
          patient={patient}
          onClose={() => setSelected(null)}
          onEdit={onEditDocument ? () => { setSelected(null); onEditDocument(selected); } : null}
          onReassigned={() => setPatient(prev => ({ ...prev, scans: (prev.scans || []).filter(s => s.id !== selected.id) }))}
        />
      )}
    </div>
  );
}
